import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { IInnovationStackApiOperation } from '../models/IInnovationStackApiOperation';
import { IInnovationStackRequest } from '../models/IInnovationStackRequest';
import { IInnovationStackResponse } from '../models/IInnovationStackResponse';
import {
  EKSAction,
  IEKSOperationResponse,
  IInnovationStackRequestLog,
  IStackRequestOperation
} from '../models/IInnovationStackRequestLog';
import { IEC2GitSyncResponse } from '../models/IEC2GitSync';
import {
  IImageReadinessResponse,
  IRepoBranchesResponse
} from '../models/IImageReadiness';
import { Utility } from '../helpers/utility';

const promiseFrom = lastValueFrom;

export class InnovationStackApis implements IInnovationStackApiOperation {
  private constructor(
    private readonly httpClient: HttpClient,
    private readonly apiV1: string,
    private readonly apiV2: string
  ) {}

  public static innovationStackApiOperation(
    httpClient: HttpClient,
    apiV1: string,
    apiV2: string
  ): IInnovationStackApiOperation {
    return new InnovationStackApis(httpClient, apiV1, apiV2);
  }

  public async createInnovationStack(
    request: IInnovationStackRequest,
    clientId: number
  ): Promise<IInnovationStackResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack`,
      queryParameters: { clientId: clientId.toString() }
    });

    return promiseFrom(
      this.httpClient.post<IInnovationStackResponse>(url, request)
    );
  }

  public async getImageStatus(
    apiBranch: string,
    portalBranch: string
  ): Promise<IImageReadinessResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack/image-status`,
      queryParameters: { apiBranch, portalBranch }
    });

    return promiseFrom(this.httpClient.get<IImageReadinessResponse>(url));
  }

  public async getRepoBranches(): Promise<IRepoBranchesResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack/branches`
    });

    return promiseFrom(this.httpClient.get<IRepoBranchesResponse>(url));
  }

  public async getInnovationStackRequestLogs(
    clientId?: number
  ): Promise<IInnovationStackRequestLog[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack/request-logs`,
      queryParameters: clientId ? { clientId: clientId.toString() } : {}
    });

    return promiseFrom(this.httpClient.get<IInnovationStackRequestLog[]>(url));
  }

  public async manageEKSStack(
    requestId: number,
    action: EKSAction,
    ttlExtendDays?: number
  ): Promise<IEKSOperationResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack/eks/${requestId}/${action}`
    });

    const body = ttlExtendDays === undefined ? {} : { ttlExtendDays };
    return promiseFrom(this.httpClient.post<IEKSOperationResponse>(url, body));
  }

  public async getRequestOperations(
    requestId: number
  ): Promise<IStackRequestOperation[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack/${requestId}/operations`
    });

    return promiseFrom(this.httpClient.get<IStackRequestOperation[]>(url));
  }

  public async manageInstanceState(
    instanceId: string,
    actionId: string
  ): Promise<{ message: string; state: string }> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack/instance`
    });

    return promiseFrom(
      this.httpClient.post<{ message: string; state: string }>(url, {
        instanceId,
        actionId
      })
    );
  }

  public async getGitBranches(
    ipAddress: string,
    workingDirectory: string
  ): Promise<{ branches: string[]; success: boolean; message?: string }> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack/ec2/branches`,
      queryParameters: { ipAddress, workingDirectory }
    });

    return promiseFrom(
      this.httpClient.get<{
        branches: string[];
        success: boolean;
        message?: string;
      }>(url)
    );
  }

  public async checkoutGitBranch(
    ipAddress: string,
    workingDirectory: string,
    branch: string
  ): Promise<IEC2GitSyncResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack/ec2/checkout-branch`
    });

    return promiseFrom(
      this.httpClient.post<IEC2GitSyncResponse>(url, {
        ipAddress,
        workingDirectory,
        branch
      })
    );
  }

  public async syncGitRepository(
    ipAddress: string,
    workingDirectory: string,
    branch?: string
  ): Promise<IEC2GitSyncResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack/ec2/git-sync`
    });

    return promiseFrom(
      this.httpClient.post<IEC2GitSyncResponse>(url, {
        ipAddress,
        workingDirectory,
        ...(branch ? { branch } : {})
      })
    );
  }

  public async restartMeasuredApiOrPortal(
    ipAddress: string,
    workingDirectory: string
  ): Promise<IEC2GitSyncResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/innovation-stack/ec2/app/restart`
    });

    return promiseFrom(
      this.httpClient.post<IEC2GitSyncResponse>(url, {
        ipAddress,
        workingDirectory
      })
    );
  }
}
